import { z } from 'zod';
import { iceConfigSchema, roomIdSchema } from "./http.js";
/**
 * The WebSocket contract. Section 9 requires validating every signalling
 * message, not just the upgrade, so every inbound frame goes through
 * `clientMessageSchema` before anything touches room state.
 *
 * Audio never appears here. This channel carries SDP, ICE and presence only.
 */
/** A hard ceiling on one frame. An SDP with many candidates is a few KB. */
export const MAX_SIGNAL_BYTES = 64 * 1024;
const sdpSchema = z.object({
    type: z.enum(['offer', 'answer']),
    /** Large enough for a real SDP, small enough not to be a memory lever. */
    sdp: z
        .string()
        .min(1)
        .max(32 * 1024),
});
const iceCandidateSchema = z.object({
    candidate: z.string().max(1024),
    sdpMid: z.string().max(64).nullable().optional(),
    sdpMLineIndex: z.number().int().min(0).max(32).nullable().optional(),
    usernameFragment: z.string().max(256).nullable().optional(),
});
/** Why a translator is or is not running, as the peer should see it. */
export const translatorStateSchema = z.enum([
    'idle',
    'connecting',
    'ready',
    /**
     * Closed on purpose after a minute nobody spoke, and waiting to reopen when
     * somebody does. Not `closed`: the peer should read "paused", not "gone",
     * and the call clock, already running, is left alone.
     */
    'asleep',
    'reconnecting',
    'failed',
    'closed',
]);
export const clientMessageSchema = z.discriminatedUnion('type', [
    /** Sent once after the socket opens, so the server can flush room state. */
    z.object({ type: z.literal('hello') }),
    z.object({ type: z.literal('sdp'), description: sdpSchema }),
    z.object({ type: z.literal('ice'), candidate: iceCandidateSchema }),
    /** Tells the room this side's translator reached `ready`; starts the clock. */
    z.object({ type: z.literal('translator-state'), state: translatorStateSchema }),
    /** Mic on/off, purely so the peer can show an indicator. */
    z.object({ type: z.literal('mic'), muted: z.boolean() }),
    /** Heartbeat. Section 9 wants a peer's disappearance noticed without a close. */
    z.object({ type: z.literal('ping'), at: z.number().int().positive() }),
    z.object({ type: z.literal('leave') }),
]);
export const peerStateSchema = z.object({
    present: z.boolean(),
    translator: translatorStateSchema,
    micMuted: z.boolean(),
});
export const roomStateSchema = z.object({
    roomId: roomIdSchema,
    seat: z.union([z.literal(0), z.literal(1)]),
    /** Seat 0 makes the first offer. One fixed initiator, so no glare. */
    isInitiator: z.boolean(),
    peer: peerStateSchema,
    /** Unix ms when both translators first went ready, or null. */
    callStartedAt: z.number().int().positive().nullable(),
    /** Unix ms the server will close the room at, once the call has started. */
    callEndsAt: z.number().int().positive().nullable(),
});
export const closeReasonSchema = z.enum([
    'peer-left',
    'time-limit',
    'peer-lost',
    'server-shutdown',
    'expired',
]);
export const serverMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('room-state'), state: roomStateSchema }),
    z.object({ type: z.literal('sdp'), description: sdpSchema }),
    z.object({ type: z.literal('ice'), candidate: iceCandidateSchema }),
    /** Both translators are up; the 15-minute budget starts here. */
    z.object({
        type: z.literal('call-started'),
        startedAt: z.number().int().positive(),
        endsAt: z.number().int().positive(),
    }),
    /** Fresh TURN credentials before the old ones lapse mid-call. */
    z.object({ type: z.literal('ice-config'), iceConfig: iceConfigSchema }),
    z.object({ type: z.literal('room-closed'), reason: closeReasonSchema }),
    z.object({ type: z.literal('pong'), at: z.number().int().positive() }),
    z.object({
        type: z.literal('error'),
        code: z.string(),
        message: z.string(),
        /** A fatal error is followed by a close; a soft one is informational. */
        fatal: z.boolean(),
    }),
]);
/** Application close codes, above the 4000 range reserved for apps. */
export const WS_CLOSE = {
    normal: 1000,
    unauthorized: 4001,
    roomGone: 4004,
    protocol: 4002,
    replaced: 4003,
};
//# sourceMappingURL=signaling.js.map